import { useState, useEffect } from 'react';

// API URL - puede ser configurada via variable de entorno
const API_URL = import.meta.env.VITE_API_URL || 'https://crowdbuy.trustnodes.workers.dev';

interface User {
  id: string;
  telegramId: number;
  fullName: string;
  role: string;
}

// Storage keys
const ACCESS_TOKEN_KEY = 'crowdbuy_admin_access_token';
const REFRESH_TOKEN_KEY = 'crowdbuy_admin_refresh_token';
const USER_KEY = 'crowdbuy_admin_user';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check for existing tokens on mount
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const userData = localStorage.getItem(USER_KEY);

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }

    setIsLoading(false);
  }, []);

  const login = (token: string, refreshToken: string, userData: User) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshAuth = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();

      if (data.success && data.data) {
        localStorage.setItem(ACCESS_TOKEN_KEY, data.data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.data.refreshToken);
        if (data.data.user) {
          localStorage.setItem(USER_KEY, JSON.stringify(data.data.user));
          setUser(data.data.user);
        }
        return true;
      }
    } catch (error) {
      console.error('Error refreshing auth:', error);
    }

    logout();
    return false;
  };

  const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

  return {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshAuth,
    getAccessToken,
  };
}

/**
 * Helper para hacer peticiones autenticadas al API
 */
export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (!accessToken) {
    throw new Error('No autenticado');
  }

  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${accessToken}`);
  headers.set('Content-Type', 'application/json');

  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
}